import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Award, BookOpen, RefreshCw, Calendar, CheckCircle, PlayCircle } from "lucide-react";
import moment from "moment";
import axios from "axios";
import ProgramCards, { EmptyState, ProgramCardSkeleton } from "./ProgramCards";
import { ALLPROGRAM, BASEURL } from "../constants";
import StudentProfileCard from "./StudentProfileCard";
import StatusWarningsCard from "./StatusWarningsCard";
import UsiRequiredModal from "./UsiRequiredModal";
import WelcomeSection from "./WelcomeSection";
import { useUsiPopup } from "../hooks/useUsiPopup";
import WarningRibbon from "./WarningRibbon";

const Program = () => {
  const [currentCourses, setCurrentCourses] = useState([]);
  const [upcomingCourses, setUpcomingCourses] = useState([]);
  const [pastCourses, setPastCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("current");
  const studentId = localStorage.getItem("studentId");
  const studentName = localStorage.getItem("studentName");
  const { showPopup, message, handleAddUsi, handleLater } = useUsiPopup(studentId);

  const getCourseData = async (studentId) => {
    try {
      const response = await axios.get(`${BASEURL}Student/${ALLPROGRAM}/Programs/${studentId}`);
      return response.data;
    } catch (error) {
      console.log("Getting courses error -> ", error);
      return [];
    }
  };

  const divideCourses = (courses) => {
    const current = [];
    const past = [];
    const upcoming = [];

    courses.forEach((course) => {
      const currentDate = moment.now();
      const startDate = moment.utc(course.startDate.trim(), "DD/MM/YYYY").toDate();
      const endDate = moment.utc(course.endDate.trim(), "DD/MM/YYYY").toDate();

      if (startDate <= currentDate && endDate >= currentDate) {
        current.push(course);
      } else if (endDate < currentDate) {
        past.push(course);
      } else if (startDate > currentDate) {
        upcoming.push(course);
      }
    });

    setCurrentCourses(current);
    setPastCourses(past);
    setUpcomingCourses(upcoming);
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      try {
        if (studentId) {
          const courses = await getCourseData(studentId);
          divideCourses(courses);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
        setRefreshing(false);
      }
    };

    if (studentId || refreshing) {
      getData();
    }
  }, [studentId, refreshing]);

  const tabs = [
    {
      id: "current",
      label: "In Progress",
      count: currentCourses.length,
      icon: PlayCircle,
      color: "blue",
      courses: currentCourses,
    },
    {
      id: "upcoming",
      label: "Upcoming",
      count: upcomingCourses.length,
      icon: Calendar,
      color: "amber",
      courses: upcomingCourses,
    },
    {
      id: "past",
      label: "Completed",
      count: pastCourses.length,
      icon: CheckCircle,
      color: "green",
      courses: pastCourses,
    },
  ];

  const getActiveTabCourses = () => {
    const activeTabData = tabs.find((tab) => tab.id === activeTab);
    return activeTabData?.courses || [];
  };

  const hasNoCourses = currentCourses.length === 0 && upcomingCourses.length === 0 && pastCourses.length === 0;

  return (
    <div className="pt-6">
      {/* <Link to="/dashboard/editStudentDetails" className="absolute top-4 right-4 text-blue-600 hover:underline z-10">
        Edit
      </Link> */}
      {/* USI Modal */}
      <UsiRequiredModal
        open={showPopup}
        message={message}
        onAddUsi={handleAddUsi}
        onLater={handleLater}
      />

      {/* Page Container */}
      <div className="container mx-auto px-4 sm:px-2 max-w-[2000px]">
        <WelcomeSection studentName={studentName} />
        {/* <div className="flex flex-col lg:flex-row gap-6 mb-6">
        <div className="flex-1">
          <StudentProfileCard />
        </div>
        <div className="flex-1">
          <StatusWarningsCard 
            currentCourses={currentCourses}
            upcomingCourses={upcomingCourses}
            pastCourses={pastCourses}
            loading={loading}
          />
        </div>
      </div> */}
      <div className="mb-2">
        <WarningRibbon/>
      </div>
        {/* Tabs Section */}
        <div className="bg-white rounded-md border border-gray-200 mb-8 shadow-lg">
          {/* Tab Headers */}
          <div className="flex border-b border-gray-100">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 flex flex-col items-center py-4 px-2 sm:px-6 relative transition-all duration-200`}
                >
                  {/* Icon and Count */}
                  <div className="flex items-center gap-2 mb-1">
                    <Icon 
                      className={`w-4 h-4 ${
                        isActive 
                          ? tab.color === 'blue' ? 'text-blue-500' 
                            : tab.color === 'amber' ? 'text-amber-500' 
                            : 'text-green-500'
                          : 'text-gray-400'
                      }`} 
                    />
                    <span 
                      className={`text-[1rem] font-bold ${
                        isActive 
                          ? tab.color === 'blue' ? 'text-blue-500' 
                            : tab.color === 'amber' ? 'text-amber-500' 
                            : 'text-green-500'
                          : 'text-gray-400'
                      }`}
                    >
                      {loading ? '-' : tab.count}
                    </span>
                  </div>
                  
                  {/* Label */}
                  <span 
                    className={`text-sm font-medium ${
                      isActive 
                        ? tab.color === 'blue' ? 'text-blue-500' 
                          : tab.color === 'amber' ? 'text-amber-500' 
                          : 'text-green-500'
                        : 'text-gray-400'
                    }`}
                  >
                    {tab.label}
                  </span>
                  
                  {/* Active Indicator */}
                  {isActive && (
                    <div 
                      className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-20 h-1 rounded-t-full
                        ${tab.color === 'blue' ? 'bg-blue-500' 
                          : tab.color === 'amber' ? 'bg-amber-500' 
                          : 'bg-green-500'
                        }`}
                    />
                  )}
                </button>
              );
            })}
          </div>

          {/* Tab Content */}
          <div className="p-6 min-h-96">
            {loading ? (
              /* Loading Skeleton */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-3 gap-6 border-gray-200 shadow-md">
                {[1, 2, 3, 4, 5].map((i) => (
                  <ProgramCardSkeleton key={i} />
                ))}
              </div>
            ) : getActiveTabCourses().length === 0 ? (
              /* Empty State for specific tab */
              <EmptyState 
                message={
                  activeTab === 'current' 
                    ? 'No programs in progress' 
                    : activeTab === 'upcoming' 
                    ? 'No upcoming programs' 
                    : 'No completed programs'
                } 
              />
            ) : (
              /* Course Grid */
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-3 gap-6 border-gray-200">
                {/* <UsiRequiredModal message={"test"} open={true}/> */}
                {getActiveTabCourses().map((course) => (
                  <ProgramCards 
                    key={`${course.courseId}-${course?.enrolmentId}`} 
                    enrolment={course} 
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Program;
